import React, { useState, useEffect } from 'react';
import { Shield, Save, Users, Check, X, Settings, Eye } from 'lucide-react';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';

const WardenPermissions = () => {
  const [wardens, setWardens] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWardens, setSelectedWardens] = useState([]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [editingWarden, setEditingWarden] = useState(null);
  const [customPermissions, setCustomPermissions] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [wardensRes, templatesRes] = await Promise.all([
        apiClient.get('/v1/admin/warden-permissions/wardens'),
        apiClient.get('/v1/admin/warden-permissions/templates')
      ]);
      
      setWardens(wardensRes.data || []);
      setTemplates(templatesRes.data.templates || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectWarden = (wardenId) => {
    setSelectedWardens(prev => 
      prev.includes(wardenId) 
        ? prev.filter(id => id !== wardenId)
        : [...prev, wardenId]
    );
  };

  const handleSelectAll = () => {
    if (selectedWardens.length === wardens.length) {
      setSelectedWardens([]);
    } else {
      setSelectedWardens(wardens.map(w => w.id));
    }
  };

  const handleBulkApplyTemplate = async (templateKey) => {
    if (selectedWardens.length === 0) {
      toast.error('Please select at least one warden');
      return;
    }

    try {
      const response = await apiClient.post('/v1/admin/warden-permissions/bulk-apply-template', {
        warden_ids: selectedWardens,
        template_key: templateKey
      });

      toast.success(`✅ Applied to ${response.data.results.successful} wardens`);
      if (response.data.results.failed > 0) {
        toast.warning(`⚠️ ${response.data.results.failed} failed`);
      }

      fetchData();
      setSelectedWardens([]);
    } catch (error) {
      // 'Bulk apply error:', error);
      toast.error('Failed to apply template');
    }
  };

  const handleApplySingleTemplate = async (wardenId, templateKey) => {
    try {
      await apiClient.post(`/v1/admin/warden-permissions/apply-template?warden_id=${wardenId}&template_key=${templateKey}`);
      toast.success('✅ Template applied successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const openCustomPermissions = (warden) => {
    setEditingWarden(warden);
    setCustomPermissions(warden.permissions);
    setShowCustomModal(true);
  };

  const saveCustomPermissions = async () => {
    try {
      await apiClient.put(`/v1/admin/warden-permissions/${editingWarden.id}`, customPermissions);
      toast.success('✅ Permissions updated successfully');
      setShowCustomModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save permissions');
    }
  };

  const togglePermission = (permissionKey) => {
    setCustomPermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey],
      is_custom: true
    }));
  };

  const permissionLabels = {
    can_update_attendance: 'Can Update Attendance',
    can_update_health: 'Can Update Health',
    can_update_activities: 'Can Update Activities',
    can_update_results: 'Can Update Results',
    can_update_academic: 'Can Update Academic / Syllabus',
    can_update_student_details: 'Can Update Student Details',
    can_update_parent_contact: 'Can Update Parent Contact',
    can_send_notifications: 'Can Send Notifications',
    can_manage_gallery: 'Can Manage Gallery'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#2C5F7F' }}>
          Assign Rights to Wardens
        </h1>
        <p className="text-gray-600">
          Manage what wardens can do with their assigned students
        </p>
      </div>

      {/* Bulk Actions */}
      {selectedWardens.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">
                {selectedWardens.length} warden(s) selected
              </span>
            </div>
            <div className="flex gap-2">
              <label className="text-sm font-medium text-blue-900 mr-2">Apply Template:</label>
              {templates.map(template => (
                <button
                  key={template.key}
                  onClick={() => handleBulkApplyTemplate(template.key)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Wardens Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedWardens.length === wardens.length && wardens.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Warden Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Contact</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Current Template</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Custom</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Quick Apply</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {wardens.map(warden => (
              <tr key={warden.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedWardens.includes(warden.id)}
                    onChange={() => handleSelectWarden(warden.id)}
                    className="w-4 h-4"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{warden.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{warden.phone}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    warden.permissions.template_name === 'Full Access' ? 'bg-green-100 text-green-800' :
                    warden.permissions.template_name === 'Records Manager' ? 'bg-blue-100 text-blue-800' :
                    warden.permissions.template_name === 'View Only' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {warden.permissions.template_name || 'Not Set'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {warden.permissions.is_custom ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <X className="w-5 h-5 text-gray-300" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleApplySingleTemplate(warden.id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="">Select template...</option>
                    {templates.map(template => (
                      <option key={template.key} value={template.key}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openCustomPermissions(warden)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Custom Permissions"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {wardens.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No wardens found
          </div>
        )}
      </div>

      {/* Permission Templates Info */}
      <div className="mt-6 grid grid-cols-4 gap-4">
        {templates.map(template => (
          <div key={template.key} className="bg-white rounded-lg shadow p-4 border">
            <h3 className="font-semibold mb-2" style={{ color: '#2C5F7F' }}>
              {template.name}
            </h3>
            <p className="text-xs text-gray-600 mb-3">{template.description}</p>
            <div className="space-y-1">
              {Object.entries(permissionLabels).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  {template.permissions[key] ? (
                    <Check className="w-3 h-3 text-green-600" />
                  ) : (
                    <X className="w-3 h-3 text-red-400" />
                  )}
                  <span className={template.permissions[key] ? 'text-gray-700' : 'text-gray-400'}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Custom Permissions Modal */}
      {showCustomModal && editingWarden && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">
                  Custom Permissions - {editingWarden.name}
                </h3>
                <button
                  onClick={() => setShowCustomModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3 mb-6">
                {Object.entries(permissionLabels).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">{label}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={customPermissions[key] || false}
                        onChange={() => togglePermission(key)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={saveCustomPermissions}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Save Permissions
                </button>
                <button
                  onClick={() => setShowCustomModal(false)}
                  className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WardenPermissions;
